import React from 'react';
import { CreateOffer_offer_promos_affiliates_boostDataForAffiliateOffer } from '@frontend/applications/AffiliatesApp/queries/types/CreateOffer';
import { Tooltip, TooltipContent, TooltipTrigger, } from '@frontend/shadcn/components/ui/tooltip';
import { TagsIcon } from '@revfluence/fresh-icons/solid';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@frontend/shadcn/components/ui/accordion';
import { Button } from '@frontend/shadcn/components/ui/button';
import { ClockIcon, TrophyIcon } from '@revfluence/fresh-icons/solid/esm';
import Tag from '@frontend/app/refresh-components/Tag';

export const MemberBoostDetails = ({ memberBoosts }: { memberBoosts: CreateOffer_offer_promos_affiliates_boostDataForAffiliateOffer[] | null }): JSX.Element => {
    if (!memberBoosts || memberBoosts.length === 0) {
        return (
          <div className="text-center py-8 text-grey-5">
            <p>No boost data available</p>
          </div>
        );
    }

    const getStatusTag = (status: string) => {
        switch (status) {
            case 'PENDING':
                return { label: 'Waiting for approval', bgColor: 'bg-yellow-2', textColor: 'text-primary' };
            case 'APPROVED':
                return { label: 'Awarded', bgColor: 'bg-green-2', textColor: 'text-primary' };
            case 'REJECTED':
                return { label: 'Rejected', bgColor: 'bg-red-2', textColor: 'text-primary' };
            default:
                return { label: 'Unknown', bgColor: 'bg-grey-2', textColor: 'text-primary' };
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'PENDING':
                return 'text-yellow-4';
            case 'APPROVED':
                return 'text-green-4';
            case 'REJECTED':
                return 'text-red-4';
            default:
                return 'text-grey-4';
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getOverallRewardStatus = (boostRewardsForAffiliate: any[]) => {
        if (!boostRewardsForAffiliate || boostRewardsForAffiliate.length === 0) {
            return { label: 'No rewards', bgColor: 'bg-grey-2', textColor: 'text-primary' };
        }

        // Check if at least one reward is waiting for input (PENDING)
        const hasPending = boostRewardsForAffiliate.some((reward) => reward.status === 'PENDING');
        if (hasPending) {
            return { label: 'Waiting for approval', bgColor: 'bg-yellow-2', textColor: 'text-primary' };
        }

        // Check if at least one reward is awarded (APPROVED)
        const hasApproved = boostRewardsForAffiliate.some((reward) => reward.status === 'APPROVED');
        if (hasApproved) {
            return { label: 'Awarded', bgColor: 'bg-green-2', textColor: 'text-primary' };
        }

        // Check if at least one reward is rejected
        const hasRejected = boostRewardsForAffiliate.some((reward) => reward.status === 'REJECTED');
        if (hasRejected) {
            return { label: 'Rejected', bgColor: 'bg-red-2', textColor: 'text-primary' };
        }

        // Default fallback
        return { label: 'Unknown', bgColor: 'bg-grey-2', textColor: 'text-primary' };
    };

    return (
      <>
        {memberBoosts.map((boostData, index) => {
                const { boost, boostRewardsForAffiliate } = boostData;
                const hasRewards = boostRewardsForAffiliate && boostRewardsForAffiliate.length > 0;

                return (
                  <Accordion key={index} type="single" collapsible className="w-full">
                    <AccordionItem value={`boost-${boost.id}`} className="pt-2  border border-grey-2 rounded-xl px-4">
                      <AccordionTrigger className="p-0 flex items-center hover:no-underline gap-2 m-0">
                        <div className="flex h-[54px] w-[54px] flex-shrink-0 items-center justify-center rounded-full bg-blue-2">
                          <TagsIcon className="h-6 w-6 text-blue-5" />
                        </div>
                        <div className="flex flex-1 items-center justify-between">
                          <h3 className="text-grey-6 font-medium flex items-center m-0">
                            {boost.label}
                            <Tooltip>
                              <TooltipTrigger>
                                <div className="flex items-center justify-center w-3 h-3 rounded-full bg-grey-2 text-grey-foreground text-[8px] font-semibold ml-2">
                                  1
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]">
                                One time
                              </TooltipContent>
                            </Tooltip>
                          </h3>
                          <Tag
                            label={getOverallRewardStatus(boostRewardsForAffiliate).label}
                            bgColor={getOverallRewardStatus(boostRewardsForAffiliate).bgColor}
                            textColor="text-primary"
                          />
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="flex flex-col gap-2 mt-2 pb-4">
                        <p className="text-grey-5 font-medium text-sm m-0">Boost Details</p>
                        <div className="border-b" />
                        <p className="text-grey-5 font-normal text-sm m-0">
                          {boost.boostType === 'DISCOUNT_BOOST' ? 'Discount Boost' : boost.boostType}
                          {boost.isActive && <span className="text-grey-5 text-xs font-normal ml-2">Active</span>}
                        </p>

                        {hasRewards ? (
                                    boostRewardsForAffiliate.map((reward, rewardIndex) => {
                                        const statusTag = getStatusTag(reward.status);
                                        const statusIconClass = getStatusIcon(reward.status);

                                        return (
                                          <div key={reward.id} className="flex flex-row items-stretch gap-3">
                                            <div className="flex flex-col items-center w-7 relative">
                                              <ClockIcon className={`h-4 w-4 ${statusIconClass}`} />
                                              {rewardIndex < boostRewardsForAffiliate.length - 1 && (
                                                <div className="flex w-px border-l-2 border-dashed border-gray-200 h-full" />
                                                    )}
                                            </div>
                                            <div className="flex-1">
                                              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                                                <span className="font-medium text-sm text-primary">
                                                  {boost.boostType === 'DISCOUNT_BOOST' ? 'Discount Code' : 'Reward'}
                                                </span>
                                                <span className="text-primary text-sm hidden sm:inline">•</span>
                                                <span className="text-primary text-sm">
                                                  {reward.status === 'APPROVED' && <TrophyIcon className="h-3 w-3 text-yellow-5 inline mr-1" />}
                                                  {reward.status === 'APPROVED' ? `Awarded on ${formatDate(reward.updatedDate)}` : statusTag.label}
                                                </span>
                                              </div>
                                              <div className="text-grey-4 text-xs mt-1">Created on {formatDate(reward.createdDate)}</div>

                                              {reward.status === 'PENDING' && (
                                                <div className="flex items-center gap-4 mt-3">
                                                  <Button className="px-8 py-2 text-base font-medium" variant="default">Approve</Button>
                                                  <button className="text-destructive text-base font-medium hover:underline focus:outline-none" type="button">Reject</button>
                                                </div>
                                                    )}

                                              {reward.status === 'APPROVED' && reward.secureCode && (
                                                <div className="text-grey-4 text-xs mt-1">Approved by Admin</div>
                                                    )}

                                              {reward.secureCode && (
                                                <div className="flex items-center justify-between bg-grey-1 rounded-2xl px-2 py-2 mt-2">
                                                  <p className="text-primary text-sm font-normal m-0">
                                                    Discount Code: {reward.secureCode.externalDiscountCode.length > 10 ? `${reward.secureCode.externalDiscountCode.slice(0, 10)}...` : reward.secureCode.externalDiscountCode}
                                                  </p>
                                                  <Tag
                                                    label={reward.secureCode.isRedeemed ? 'Redeemed' : 'waiting for creator'}
                                                    bgColor={reward.secureCode.isRedeemed ? 'bg-green-2' : 'bg-blue-2'}
                                                    textColor="text-primary"
                                                  />
                                                </div>
                                                    )}

                                              {reward.secureCode?.isRedeemed && (
                                                <Button variant="outline" className="w-max mt-2">View Order</Button>
                                                    )}
                                            </div>
                                          </div>
                                        );
                                    })
                                ) : (
                                  <div className="text-grey-4 text-sm py-4 text-center">
                                    No rewards available for this boost
                                  </div>
                                )}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                );
            })}
      </>
    );
};
export default MemberBoostDetails;
